import { useCallback, useEffect, useState } from "react";
import { api, notifyError } from "../lib/api";
import { fmtMoney } from "../lib/currency";
import {
  PAYMENT_SOURCES,
  paymentMethodLabel,
} from "../lib/paymentMethods";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Wallet, Loader2, PackageOpen, Boxes } from "lucide-react";

/**
 * Ödemeler ekranı (şartname #7/#24).
 *
 * Artık iki kaynağı birlikte gösterir:
 *   • Emanet ödemeleri (source='emanet')
 *   • Satış/Tahsilat ödemeleri (source='satis')
 * Filtre: Tümü / Emanetler / Satış-Tahsilat. Mevcut tablo yapısı, toplam
 * kartı, tarih ve müşteri ilişkisi korunmuştur; "Yöntem" kolonu artık standart
 * "Tahsilat Şekli" etiketlerini gösterir.
 */
export default function Payments() {
  const { defaultCurrency } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/payments?source=${source}`);
      setList(data);
    } catch (e) {
      notifyError(e);
    } finally {
      setLoading(false);
    }
  }, [source]);

  useEffect(() => {
    load();
  }, [load]);

  const total = list.reduce((acc, p) => acc + (p.amount || 0), 0);
  const emanetTotal = list
    .filter((p) => (p.source || "emanet") === "emanet")
    .reduce((acc, p) => acc + (p.amount || 0), 0);
  const satisTotal = list
    .filter((p) => p.source === "satis")
    .reduce((acc, p) => acc + (p.amount || 0), 0);

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto space-y-6" data-testid="payments-page">
      <div>
        <div className="label-eyebrow text-amber-600">Tahsilat</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">Ödemeler</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Emanet ve Satış/Stok tahsilat hareketleri ve toplamlar.
        </p>
      </div>

      <Card className="rounded-sm border-border bg-foreground text-background p-6 flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 bg-amber-500 text-white rounded-sm flex items-center justify-center">
            <Wallet className="h-6 w-6" />
          </div>
          <div>
            <div className="label-eyebrow text-amber-400">
              {source === "emanet"
                ? "Emanet Tahsilatı"
                : source === "satis"
                ? "Satış Tahsilatı"
                : "Toplam Tahsilat"}
            </div>
            <div className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-1" data-testid="payments-total">
              {fmtMoney(total, defaultCurrency)}
            </div>
          </div>
        </div>
        {source === "all" && (
          <div className="flex flex-wrap gap-6 sm:ml-auto text-sm">
            <div>
              <div className="label-eyebrow text-background/60">Emanetler</div>
              <div className="font-mono font-semibold mt-1" data-testid="payments-total-emanet">
                {fmtMoney(emanetTotal, defaultCurrency)}
              </div>
            </div>
            <div>
              <div className="label-eyebrow text-background/60">Satış/Tahsilat</div>
              <div className="font-mono font-semibold mt-1" data-testid="payments-total-satis">
                {fmtMoney(satisTotal, defaultCurrency)}
              </div>
            </div>
          </div>
        )}
      </Card>

      <Tabs value={source} onValueChange={setSource}>
        <TabsList className="rounded-sm bg-muted">
          {PAYMENT_SOURCES.map((s) => (
            <TabsTrigger
              key={s.value}
              value={s.value}
              className="rounded-sm"
              data-testid={`payments-source-${s.value}`}
            >
              {s.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : list.length === 0 ? (
        <Card className="p-12 text-center rounded-sm">
          <Wallet className="h-10 w-10 mx-auto text-muted-foreground" />
          <div className="font-display font-bold text-lg mt-3">Ödeme yok</div>
          <p className="text-sm text-muted-foreground mt-2">
            Bu filtrede kayıtlı tahsilat bulunmuyor.
          </p>
        </Card>
      ) : (
        <Card className="rounded-sm border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-foreground text-background">
                <tr>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Müşteri</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Kaynak</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Tutar</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Tahsilat Şekli</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Tarih</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {list.map((p) => {
                  const isSale = p.source === "satis";
                  return (
                    <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                      <td className="px-4 py-3 font-semibold">{p.customer_name || "—"}</td>
                      <td className="px-4 py-3">
                        <Badge
                          variant="outline"
                          className={`rounded-sm text-[10px] uppercase tracking-wider ${
                            isSale
                              ? "border-sky-500/40 text-sky-700"
                              : "border-amber-500/40 text-amber-700"
                          }`}
                          data-testid={`payment-source-${p.id}`}
                        >
                          {isSale ? (
                            <><Boxes className="h-3 w-3 mr-1" /> Satış</>
                          ) : (
                            <><PackageOpen className="h-3 w-3 mr-1" /> Emanet</>
                          )}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 font-mono">{fmtMoney(p.amount, p.currency || defaultCurrency)}</td>
                      <td className="px-4 py-3" data-testid={`payment-method-${p.id}`}>
                        {paymentMethodLabel(p.method)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                        {new Date(p.date).toLocaleString("tr-TR")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
